import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { SplitText } from "gsap/dist/SplitText";

gsap.registerPlugin(ScrollTrigger, SplitText);

/**
 * Scroll Animations.
 *
 * Sistema estendibile per animazioni di entrata su scroll.
 *
 * Architettura:
 *  - `effects` è un registry di preset {selector, animateFn}
 *  - Per ogni preset, scansiona il DOM e crea uno ScrollTrigger per elemento
 *  - Stato iniziale gestito via CSS (assets/src/scss/utils/anim.scss) per evitare FOUC
 *  - One-shot: l'animazione scatta una sola volta all'entrata in viewport
 *  - Rispetta prefers-reduced-motion (skippa l'animazione)
 *
 * Preset disponibili:
 *  - .anim-fade-up        — titoli: fade + slide-up + blur 8px, 1.8s
 *  - .anim-fade-up-text   — paragrafi: SplitText riga-per-riga, 1.2s + stagger 0.1s
 *  - .anim-image-reveal   — immagini: fade + scale-out 1.1→1 + blur, 1.5s
 *
 * Modificatori delay (combinabili con qualsiasi preset):
 *  - .anim-delay-1   → +0.2s
 *  - .anim-delay-2   → +0.4s
 *  - .anim-delay-3   → +0.6s
 *  - .anim-delay-4   → +0.8s
 *  - .anim-delay-5   → +1.0s
 *
 * Estendere:
 *  effects.push({ selector: ".anim-image-reveal", animateFn: (el) => { ... } });
 *
 * Uso (Gutenberg → "Classi CSS aggiuntive"):
 *  <h2 class="anim-fade-up">Titolo</h2>
 *  <h2 class="anim-fade-up anim-delay-2">Titolo ritardato di 0.4s</h2>
 *  <p class="anim-fade-up-text">Paragrafo lungo che si rivelerà riga per riga.</p>
 */

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Legge la classe `.anim-delay-N` (N da 1 a 5) dall'elemento e restituisce
 * il delay corrispondente in secondi. Step da 0.2s, max 1.0s.
 * Se non c'è nessuna classe delay, ritorna 0.
 */
const getDelay = (el) => {
  if (el.classList.contains("anim-delay-5")) return 1.0;
  if (el.classList.contains("anim-delay-4")) return 0.8;
  if (el.classList.contains("anim-delay-3")) return 0.6;
  if (el.classList.contains("anim-delay-2")) return 0.4;
  if (el.classList.contains("anim-delay-1")) return 0.2;
  return 0;
};

// ============================================================================
// EFFECT: .anim-fade-up
// Fade alpha + slide-up + slight-left + blur clearing
// ============================================================================

const animateFadeUp = (el) => {
  const delay = getDelay(el);

  gsap.to(el, {
    opacity: 1,
    y: 0,
    duration: 1.8,
    delay,
    ease: "power2.out",
    scrollTrigger: {
      trigger: el,
      start: "top 85%",
      toggleActions: "play none none none",
    },
  });

  // Blur leggermente più rapido (finisce prima dell'opacity) per stabilizzare
  // l'elemento mentre il fade continua a portarlo a piena visibilità
  gsap.to(el, {
    filter: "blur(0px)",
    duration: 1.2,
    delay,
    ease: "power2.out",
    scrollTrigger: {
      trigger: el,
      start: "top 85%",
      toggleActions: "play none none none",
    },
  });
};

// ============================================================================
// EFFECT: .anim-fade-up-text
// SplitText divide in righe; ogni riga fade alpha + slide-up + lieve blur.
// Stagger 0.1s tra righe per non rallentare la lettura.
// autoSplit:true gestisce automaticamente il resize re-splittando le righe.
// ============================================================================

const animateFadeUpText = (el) => {
  const delay = getDelay(el);

  // SplitText con autoSplit gestisce ridimensionamenti automaticamente.
  // L'animazione va creata dentro onSplit() per essere riapplicata dopo
  // ogni nuovo split (caricamento font, resize).
  SplitText.create(el, {
    type: "lines",
    linesClass: "line",
    mask: "lines", // wrappa ogni line in un parent con overflow:hidden
    autoSplit: true,
    onSplit: (instance) => {
      return gsap.to(instance.lines, {
        opacity: 1,
        y: 0,
        filter: "blur(0px)",
        duration: 1.2,
        delay,
        ease: "power2.out",
        stagger: 0.1,
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          toggleActions: "play none none none",
        },
      });
    },
  });
};

// ============================================================================
// EFFECT: .anim-image-reveal
// Fade alpha + scale-out (1.1→1) + blur clearing.
// Più "fisico" del titolo: la scale dà sensazione di "respiro" sull'immagine.
// ============================================================================

const animateImageReveal = (el) => {
  const delay = getDelay(el);

  gsap.to(el, {
    opacity: 1,
    scale: 1,
    duration: 1.5,
    delay,
    ease: "power2.out",
    scrollTrigger: {
      trigger: el,
      start: "top 85%",
      toggleActions: "play none none none",
    },
  });

  // Blur leggermente più rapido per stabilizzare l'immagine
  gsap.to(el, {
    filter: "blur(0px)",
    duration: 1.0,
    delay,
    ease: "power2.out",
    scrollTrigger: {
      trigger: el,
      start: "top 85%",
      toggleActions: "play none none none",
    },
  });
};

// ============================================================================
// REGISTRY effetti — estendibile in futuro
// ============================================================================

const effects = [
  { selector: ".anim-fade-up", animateFn: animateFadeUp },
  { selector: ".anim-fade-up-text", animateFn: animateFadeUpText },
  { selector: ".anim-image-reveal", animateFn: animateImageReveal },
];

// ============================================================================
// INIT
// ============================================================================

const initScrollAnimations = () => {
  // Marca il body: il CSS attiva lo stato iniziale solo quando JS è caricato
  // (pattern FOUC-safe — senza JS gli elementi sono visibili)
  document.body.classList.add("js-loaded");

  // Reduced motion: niente animazioni, gli elementi sono mostrati subito dal CSS
  if (prefersReducedMotion()) return;

  effects.forEach(({ selector, animateFn }) => {
    const elements = document.querySelectorAll(selector);
    elements.forEach(animateFn);
  });
};

export default () => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initScrollAnimations);
  } else {
    initScrollAnimations();
  }
};